import { useEffect } from "react";
import { attachRecoveryStream } from "../../utils/streamingUtils";
import useStreamingStateStore from "../../store/useStreamingStateStore";
import useGenerateApiSignal from "../../store/useGenerateApiSignal";
import QUERYKEYS from "../../api/queryKeys";

/**
 * Recovery effect for page refresh / tab discard.
 *
 * Fires when Zustand state has been wiped (pendingGenerations is empty). If the
 * latest message has an empty model answer, checks the backend generation status
 * endpoint to decide what to do:
 *
 *   - running / queued  → re-hydrate store + reattach SSE stream (with poll fallback)
 *   - terminal          → invalidate messages cache so completed content is shown
 *   - 404 / error       → no-op (answer is genuinely empty or generation not found)
 *
 * The existing useReconnectStream hook handles navigation away/back when
 * pendingGenerations is already populated — this hook is the fallback for when
 * that in-memory state was lost.
 */
export function useRefreshRecovery({
  chat_id,
  space_id,
  messages,
  isDataReady,
  apiUrl,
  queryClient,
  setIsLatestMessageLoading,
  setGenerateStatus,
}) {
  const pendingGenerationIds = useStreamingStateStore((state) =>
    Object.keys(state.pendingGenerations).sort().join('|')
  );
  const activeGenerateId = useGenerateApiSignal(
    (state) => state.signals[chat_id]?.generate_id
  );

  useEffect(() => {
    if (!isDataReady || !messages.length || !chat_id) return;

    // Skip if in-memory state is intact — useReconnectStream handles it.
    if (activeGenerateId) return;

    // Find the latest model answer that is not in a terminal state. We
    // intentionally do NOT gate on empty content — for long-running
    // skills (e.g. presentation generation), the LLM may have streamed planning
    // text before the SSE dropped mid-tool-execution. The backend status API
    // call below distinguishes genuinely complete answers (returns 'complete' →
    // cache invalidate) from still-running ones (returns 'running' → reattach).
    const TERMINAL_STATUSES = ['stopped', 'failed', 'interrupted', 'partial', 'complete'];
    let candidate = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (!msg.answers?.length) continue;
      for (let j = msg.answers.length - 1; j >= 0; j--) {
        const ans = msg.answers[j];
        if (
          (!ans?.role || ans.role === 'model') &&
          ans?.id &&
          !TERMINAL_STATUSES.includes(ans?.status)
        ) {
          candidate = { msg, answer: ans };
          break;
        }
      }
      if (candidate) break;
    }
    if (!candidate) return;

    const { msg, answer } = candidate;
  if (useStreamingStateStore.getState().pendingGenerations[msg.id]) return;
    const generationId = answer.id;

    // NOTE: we deliberately do NOT pre-set the generate signal here. Doing so
    // (before confirming the generation is actually running) orphaned the
    // signal under React StrictMode's double-mount: mount 1 set the signal and
    // started the fetch, the StrictMode cleanup aborted the fetch (AbortError →
    // early return, signal left set), and mount 2's "signal already set" guard
    // then bailed — so the status check never ran, the answer was never marked
    // terminal, and the skeleton beamed forever. The signal is now set only in
    // the running/queued branch below, where a live generation genuinely needs
    // it for the stop button.
    let cancelled = false;

    // Container for async-created cleanup so the synchronous cleanup function
    // can call it even though it is assigned inside the async IIFE.
    const controllers = { fetch: new AbortController(), recovery: null };

    // Mark the candidate answer terminal in the cache IFF it is still
    // empty. This lets SingleMessage exit recovery when the generation record
    // is terminal but the answer never received that status. Only touches empty
    // answers, so an answer that actually has content is never overwritten.
    // Returns true if it patched an empty answer.
    //
    // CHAT_MESSAGES is a plain useQuery with shape { messages: [...] } (NOT an
    // infinite query with { pages } — patching prev.pages silently no-op'd,
    // which is exactly why the skeleton used to beam forever).
    const markAnswerStatusIfEmpty = (status) => {
      let patched = false;
      queryClient.setQueryData(
        [QUERYKEYS.CHAT_MESSAGES, chat_id, space_id],
        (prev) => {
          if (!prev?.messages) return prev;
          return {
            ...prev,
            messages: prev.messages.map((m) => {
              if (m.id !== msg.id) return m;
              return {
                ...m,
                answers: m.answers?.map((a) => {
                  if (a.id === answer.id && !a.content?.trim()) {
                    patched = true;
                    return { ...a, status };
                  }
                  return a;
                }),
              };
            }),
          };
        }
      );
      return patched;
    };

    (async () => {
      // Retry up to 3 times with exponential backoff (1s, 2s, 4s) to survive
      // transient failures during backend deploys or network blips.
      const MAX_RETRIES = 3;
      const statusUrl = new URL(`${apiUrl}/spark/generation/${generationId}`);
      statusUrl.searchParams.set('chat_id', chat_id);
      if (space_id) statusUrl.searchParams.set('space_id', space_id);
      let data = null;
      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
          if (attempt > 0) {
            await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt - 1)));
          }
          if (controllers.fetch.signal.aborted) return;
          const sessionToken = localStorage.getItem('session_token') || '';
          const resp = await fetch(statusUrl.toString(), {
            method: 'GET',
            headers: { 'X-Session-Token': sessionToken },
            signal: controllers.fetch.signal,
          });
          if (resp.ok) {
            data = await resp.json();
            break;
          }
        } catch (err) {
          if (err.name === 'AbortError') return;
          if (attempt === MAX_RETRIES - 1) {
            return;
          }
        }
      }
      if (!data) {
        return;
      }

      const status = data?.status;

      if (cancelled) return;

      if (status === 'running' || status === 'queued') {
        // Re-hydrate store so that if the user navigates away and back, the
        // existing useReconnectStream hook can pick it up without hitting the
        // backend again.
        useStreamingStateStore.getState().setGenerationId(msg.id, generationId);
        useGenerateApiSignal.getState().addSignal(chat_id, {
          generate_id: msg.id,
          generation_id: generationId,
        });

        // Start from seq=0 — the stream endpoint replays all persisted events
        // so no events are missed. latest_seq is not returned by the status endpoint.
        controllers.recovery = attachRecoveryStream({
          generationId,
          messageId: msg.id,
          afterSeq: 0,
          apiUrl,
          queryClient,
          chat_id,
          space_id,
          setIsLatestMessageLoading,
          setGenerateStatus,
        });

      } else if (['complete', 'failed', 'stopped', 'interrupted'].includes(status)) {
        // Generation finished while the user was away. Retract the optimistic
        // signal. If the answer is empty, mirror the authoritative generation
        // status into the cache; refetching a statusless answer would otherwise
        // leave the recovery skeleton beaming with no Stop button.
        useGenerateApiSignal.getState().removeSignal(chat_id);
        if (!markAnswerStatusIfEmpty(status)) {
          queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chat_id, space_id] });
        }
      } else if (answer.content?.trim()) {
        queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chat_id, space_id] });
      }
    })();

    return () => {
      cancelled = true;
      controllers.fetch.abort();
      controllers.recovery?.();
    };
  // messages is memoized by MainContent and changes when the current chat query
  // resolves after navigation or cache hydration.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat_id, isDataReady, messages, activeGenerateId, pendingGenerationIds]);
}
